// ToDo: If you are touching this file, please read the following:
// ToDo: refactor this component to use the new design system components and remove inline styling
import { Stack, Flex, Divider, createStyles } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { Fragment, useMemo, useState, useCallback } from 'react';
import { ContestType, TiebreakerType } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';

import { LinkButton, Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { CurrencyConvertor } from '~/components/utils/formatters';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import dt from '~/testing';
import { LeagueAlias } from '~/domains/common/enums/league-alias.enum';

import { Section, SectionContent, SectionTitle } from '../components';

type ContestSettingsProps = {
  contestData: FullContest;
  contestSlates?: ContestSlate[];
  isSidePanel?: boolean;
};

const useStyles = createStyles((theme) => ({
  grid: {
    gap: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.gray[1]}`,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    justifyContent: 'space-between',
    [theme.fn.smallerThan('md')]: {
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
  },
}));

function ContestSettings({ contestData, contestSlates, isSidePanel }: ContestSettingsProps) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');
  const [isFlexDoublePicksModalOpen, setIsFlexDoublePicksModalOpen] = useState(false);
  const [isEntryRevivalModalOpen, setIsEntryRevivalModalOpen] = useState(false);

  const settings = contestData.settings as FullContest['settings'];
  const slateLabel = settings?.slateLabel || 'Slate';
  const slateLabelLower = slateLabel.toLowerCase();
  const slateGroupSubstitutes = settings?.slateGroupSubstitutes;
  const isNFL = contestData.league?.alias === LeagueAlias.NFL;
  const isPlayerOneAndDone = contestData.contest_type === ContestType.PLAYER_ONE_AND_DONE;
  const isTeamBased = [ContestType.TEAM_PICK_EM, ContestType.TEAM_SURVIVOR].includes(
    contestData.contest_type
  );

  const getPickReuseLimitDisplay = useCallback(
    (pickReuseLimit?: number) => {
      let displayValue = '';
      const isUnlimited = pickReuseLimit === null || pickReuseLimit === undefined;
      if (pickReuseLimit === 0) {
        displayValue = t('detail.contestSettings.values.once');
      } else if (pickReuseLimit === 1) {
        displayValue = t('detail.contestSettings.values.twice');
      } else if (isUnlimited) {
        displayValue = t('detail.contestSettings.values.unlimited');
      } else {
        displayValue = t('detail.contestSettings.values.times', { count: pickReuseLimit + 1 });
      }

      if (isPlayerOneAndDone && !isUnlimited) {
        const reusePeriodLabel =
          settings?.pickReuseLimitType === 'segment'
            ? t('detail.contestSettings.values.perSegment')
            : t('detail.contestSettings.values.perSeason');
        displayValue += ` (${reusePeriodLabel})`;
      }

      return displayValue;
    },
    [isPlayerOneAndDone, settings?.pickReuseLimitType, t]
  );

  const getDoublePickWeeks = useCallback(() => {
    if (!contestSlates || !settings?.expectedPicksCount) return [];

    const normalPicksCount = settings?.expectedPicksCount;
    const stepUpSlates = settings?.stepUp?.slates || [];

    if (stepUpSlates.length === 0) return [];

    const isTeamSurvivor = contestData.contest_type === ContestType.TEAM_SURVIVOR;
    const doublePickStepUps = (() => {
      if (isTeamSurvivor) {
        return normalPicksCount === 1
          ? stepUpSlates.filter((stepUpSlate) => stepUpSlate.expectedPicksCount === 2)
          : [];
      }
      return stepUpSlates.filter(
        (stepUpSlate) => stepUpSlate.expectedPicksCount > normalPicksCount
      );
    })();

    const doublePickSlateNames = doublePickStepUps
      .map((stepUpSlate) => {
        const slate = contestSlates.find((s) => s.id === stepUpSlate.slateId);
        return slate?.name;
      })
      .filter(Boolean) as string[];

    return doublePickSlateNames;
  }, [
    contestData.contest_type,
    contestSlates,
    settings?.expectedPicksCount,
    settings?.stepUp?.slates,
  ]);

  const getFlexibleDoublePicksRules = () => {
    if (!settings?.flexDoublePicks?.rules || !contestSlates) return [];

    return settings?.flexDoublePicks?.rules.map((rule) => {
      const slate = contestSlates.find((s) => s.id === rule.slateId);
      const slateName = slate?.name || rule.slateId;
      return {
        slateName,
        percentage: rule.minPercentageRemaining,
        displayText: t('detail.contestSettings.modal.flexibleDoublePicks.entrantsRemain', {
          percentage: rule.minPercentageRemaining,
        }),
      };
    });
  };

  const getEntryRevivalInfo = () => {
    if (!settings?.buybacks || !contestSlates) return null;

    const deadlineSlate = contestSlates.find((s) => s.id === settings.buybacks?.deadline);
    const deadlineSlateName = deadlineSlate?.name || settings.buybacks?.deadline;

    return {
      deadline: deadlineSlateName,
      fee: settings.buybacks?.fixedCostInDollars || 0,
      maxBuybacks: settings.buybacks?.maxBuybacksPerEntry,
    };
  };

  const getMaxAutoPicks = useCallback(() => {
    if (settings?.autoPicksSettings?.maxAutoPicks === null)
      return t('detail.contestSettings.values.unlimited');
    return t('detail.contestSettings.values.enabledWithMax', {
      max: settings?.autoPicksSettings?.maxAutoPicks,
    });
  }, [settings?.autoPicksSettings?.maxAutoPicks, t]);

  const values = useMemo(() => {
    const doublePickWeeks = getDoublePickWeeks();
    const contestSettingsItems = settings?.contestSettingsItems || [];
    const tiebreakerType = settings?.tiebreakers?.type;
    const tiebreakerTooltip = match<TiebreakerType | 'slate' | undefined>(tiebreakerType)
      .with(TiebreakerType.CUMULATIVE_WIN_MARGIN, () =>
        t('detail.contestSettings.tooltip.tiebreakerCumulativeWinMargin')
      )
      .with('slate', () => t('detail.contestSettings.tooltip.tiebreakerSlate'))
      .otherwise(() => t('detail.contestSettings.tooltip.tiebreaker'));

    return [
      ...contestSettingsItems.map((item, index) => ({
        'data-test-id': `${dt.contestDetail.section.contestSettings?.title}.templateDefined.${index}`,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {item.title}
            {item.description ? (
              <Tooltip
                withinPortal
                withArrow
                multiline
                labels={[
                  {
                    label: <Text variant="body-medium">{item.description}</Text>,
                  },
                ]}
              >
                <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
              </Tooltip>
            ) : null}
          </Flex>
        ),
        value: item.value,
      })),
      settings?.pickGoal?.type && {
        'data-test-id': dt.contestDetail.section.contestSettings?.pickObjective,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {t('detail.contestSettings.label.pickObjective')}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: (
                    <Text variant="body-medium">
                      {t('detail.contestSettings.tooltip.pickObjective')}
                    </Text>
                  ),
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value:
          settings?.pickGoal?.type === 'lose'
            ? t('detail.contestSettings.values.pickLosers')
            : t('detail.contestSettings.values.pickWinners'),
      },
      {
        'data-test-id': dt.contestDetail.section.contestSettings?.teamUseLimit,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {isTeamBased
              ? t('detail.contestSettings.label.teamUseLimit')
              : t('detail.contestSettings.label.playerUseLimit')}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: (
                    <Text variant="body-medium">
                      {isTeamBased
                        ? t('detail.contestSettings.tooltip.teamUseLimit')
                        : t('detail.contestSettings.tooltip.playerUseLimit')}
                    </Text>
                  ),
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value: getPickReuseLimitDisplay(settings?.pickReuseLimit),
      },
      ...(isNFL && settings?.postSeasonPickReuseReset !== undefined
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.resetTeamsForPlayoffs,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.resetTeams')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.resetTeams')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: settings.postSeasonPickReuseReset
                ? t('detail.contestSettings.values.enabled')
                : t('detail.contestSettings.values.disabled'),
            },
          ]
        : []),
      ...(doublePickWeeks.length > 0
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.doublePickWeeks,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.doublePickWeeks', {
                    slateLabel: slateLabelLower,
                  })}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.doublePickWeeks', {
                              slateLabel,
                            })}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: doublePickWeeks.join(', '),
            },
          ]
        : []),
      ...(settings?.flexDoublePicks
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.flexibleDoublePicks,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.flexibleDoublePicks')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.flexibleDoublePicks')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: (
                <LinkButton
                  variant="info-fill"
                  size="medium"
                  onClick={() => setIsFlexDoublePicksModalOpen(true)}
                >
                  {t('detail.contestSettings.buttons.viewMoreInfo')}
                </LinkButton>
              ),
            },
          ]
        : []),
      ...(settings?.autoPicksSettings
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.automaticPicks,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.automaticPicks')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.automaticPicks')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: getMaxAutoPicks(),
            },
          ]
        : []),
      ...(settings?.revivals
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.contestRevivals,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.contestRevivals')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.contestRevivals')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: settings.revivals.enabled
                ? t('detail.contestSettings.values.enabled')
                : t('detail.contestSettings.values.disabled'),
            },
          ]
        : []),
      ...(settings?.additionalLives !== undefined
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.entryLives,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.entryLives')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.entryLives')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: t('detail.contestSettings.values.totalLives__hasPlural', {
                count: (settings?.additionalLives || 0) + 1,
              }),
            },
          ]
        : []),
      ...(settings?.buybacks
        ? [
            {
              'data-test-id': dt.contestDetail.section.contestSettings?.entryRevival,
              label: (
                <Flex align="center" gap={theme.other.spacing._4}>
                  {t('detail.contestSettings.label.entryRevival')}
                  <Tooltip
                    withinPortal
                    withArrow
                    multiline
                    labels={[
                      {
                        label: (
                          <Text variant="body-medium">
                            {t('detail.contestSettings.tooltip.entryRevival')}
                          </Text>
                        ),
                      },
                    ]}
                  >
                    <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
                  </Tooltip>
                </Flex>
              ),
              value: (
                <LinkButton
                  variant="info-fill"
                  size="medium"
                  onClick={() => setIsEntryRevivalModalOpen(true)}
                >
                  {t('detail.contestSettings.buttons.viewMoreInfo')}
                </LinkButton>
              ),
            },
          ]
        : []),
      settings?.alternates && {
        'data-test-id': dt.contestDetail.section.contestSettings?.alternates,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {t('detail.contestSettings.label.alternates')}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: (
                    <Text variant="body-medium">
                      {t('detail.contestSettings.tooltip.alternates')}
                    </Text>
                  ),
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value: t('detail.contestSettings.values.enabled'),
      },
      settings?.tiebreakers && {
        'data-test-id': dt.contestDetail.section.contestSettings?.tiebreakers,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {t('detail.contestSettings.label.tiebreaker')}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: <Text variant="body-medium">{tiebreakerTooltip}</Text>,
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value: t('detail.contestSettings.values.enabled'),
      },
      slateGroupSubstitutes && {
        'data-test-id': dt.contestDetail.section.contestSettings?.slateGroupSubstitutes,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {slateGroupSubstitutes.label}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: <Text variant="body-medium">{slateGroupSubstitutes.description}</Text>,
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value: t('detail.contestSettings.values.enabled'),
      },
      isTeamBased && {
        'data-test-id': dt.contestDetail.section.contestSettings?.pickScoring,
        label: (
          <Flex align="center" gap={theme.other.spacing._4}>
            {t('detail.contestSettings.label.pickScoring')}
            <Tooltip
              withinPortal
              withArrow
              multiline
              labels={[
                {
                  label: (
                    <Text variant="body-medium">
                      {t('detail.contestSettings.tooltip.pickScoring')}
                    </Text>
                  ),
                },
              ]}
            >
              <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} size={16} />
            </Tooltip>
          </Flex>
        ),
        value:
          settings?.teamBasedScoringOptions?.option === 'AGAINST_THE_SPREAD'
            ? t('detail.contestSettings.values.againstTheSpread')
            : t('detail.contestSettings.values.straightUp'),
      },
    ].filter(Boolean);
  }, [
    getDoublePickWeeks,
    getPickReuseLimitDisplay,
    isNFL,
    isTeamBased,
    settings?.additionalLives,
    settings?.alternates,
    settings?.autoPicksSettings,
    settings?.buybacks,
    settings?.flexDoublePicks,
    settings?.pickGoal?.type,
    settings?.pickReuseLimit,
    settings?.postSeasonPickReuseReset,
    settings?.revivals,
    settings?.teamBasedScoringOptions,
    settings?.contestSettingsItems,
    settings?.tiebreakers,
    slateGroupSubstitutes,
    slateLabelLower,
    t,
    getMaxAutoPicks,
    slateLabel,
    theme.colors.gray,
  ]);

  if (values.length === 0) return null;

  return (
    <Section>
      <SectionTitle
        data-test-id={dt.contestDetail.section.contestSettings?.title}
        isSidePanel={isSidePanel}
      >
        {t('detail.contestSettings.title')}
      </SectionTitle>
      <SectionContent isSidePanel={isSidePanel}>
        {values.map(({ label, value, ...rest }) => (
          <Fragment key={rest['data-test-id']}>
            <Flex className={classes.grid} data-test-id={rest['data-test-id']}>
              <Text sx={{ flex: 1 }} variant="body-medium">
                {label}
              </Text>
              <Text sx={{ flex: 2 }} ta={{ base: 'left', md: 'right' }} variant="subhead-medium">
                {value}
              </Text>
            </Flex>
          </Fragment>
        ))}
      </SectionContent>

      {/* Flexible Double Picks Modal */}
      {settings?.flexDoublePicks && (
        <DialogContainer
          isOpen={isFlexDoublePicksModalOpen}
          onClose={() => setIsFlexDoublePicksModalOpen(false)}
          lockScroll
        >
          <Dialog
            type={{
              desktop: 'modal',
              mobile: 'drawer',
            }}
            isOpen={isFlexDoublePicksModalOpen}
            onClose={() => setIsFlexDoublePicksModalOpen(false)}
          >
            <Stack spacing="lg" w={{ base: '100%', md: 400 }} p="md">
              <Flex justify="center" align="center">
                <Title variant="headline-large" align="center">
                  {t('detail.contestSettings.modal.flexibleDoublePicks.title')}
                </Title>
              </Flex>

              <Stack spacing="sm">
                {getFlexibleDoublePicksRules().map((rule, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Fragment key={`${rule.slateName}-${index}`}>
                    <Flex justify="space-between" align="center">
                      <Text>{rule.slateName}:</Text>
                      <Text>
                        <strong>{rule.displayText}</strong>
                      </Text>
                    </Flex>
                    <Divider color={theme.colors.gray2[0]} />
                  </Fragment>
                ))}
              </Stack>

              <Button
                variant="primary-fill"
                size="medium"
                fullWidth
                onClick={() => setIsFlexDoublePicksModalOpen(false)}
              >
                {t('detail.contestSettings.buttons.gotIt')}
              </Button>
            </Stack>
          </Dialog>
        </DialogContainer>
      )}

      {/* Entry Revival Modal */}
      {settings?.buybacks && (
        <DialogContainer
          isOpen={isEntryRevivalModalOpen}
          onClose={() => setIsEntryRevivalModalOpen(false)}
          lockScroll
        >
          <Dialog
            type={{
              desktop: 'modal',
              mobile: 'drawer',
            }}
            isOpen={isEntryRevivalModalOpen}
            onClose={() => setIsEntryRevivalModalOpen(false)}
          >
            <Stack spacing="lg" w={{ base: '100%', md: 400 }} p="md">
              <Flex justify="center" align="center">
                <Title variant="headline-large" align="center">
                  {t('detail.contestSettings.modal.entryRevival.title')}
                </Title>
              </Flex>

              {(() => {
                const revivalInfo = getEntryRevivalInfo();
                if (!revivalInfo) return null;

                return (
                  <Stack spacing="sm">
                    <Flex justify="space-between" align="center">
                      <Text>
                        <strong>
                          {t('detail.contestSettings.modal.entryRevival.revivalDeadline')}
                        </strong>
                      </Text>
                      <Flex gap={theme.other.spacing._4}>
                        <Text>{t('detail.contestSettings.modal.entryRevival.deadlineLabel')}</Text>
                        <Text>{revivalInfo.deadline}</Text>
                      </Flex>
                    </Flex>
                    <Divider color={theme.colors.gray2[0]} />

                    <Flex justify="space-between" align="center">
                      <Text>
                        <strong>{t('detail.contestSettings.modal.entryRevival.revivalFee')}</strong>
                      </Text>
                      <Text>{CurrencyConvertor(revivalInfo.fee)}</Text>
                    </Flex>
                    <Divider color={theme.colors.gray2[0]} />

                    <Flex justify="space-between" align="center">
                      <Text>
                        <strong>
                          {t('detail.contestSettings.modal.entryRevival.revivalsPerEntry')}
                        </strong>
                      </Text>
                      <Text>{revivalInfo.maxBuybacks}</Text>
                    </Flex>
                  </Stack>
                );
              })()}

              <Button
                variant="primary-fill"
                size="medium"
                fullWidth
                onClick={() => setIsEntryRevivalModalOpen(false)}
              >
                {t('detail.contestSettings.buttons.gotIt')}
              </Button>
            </Stack>
          </Dialog>
        </DialogContainer>
      )}
    </Section>
  );
}

export default ContestSettings;
